import axios from 'axios';

const BACKEND_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API_BASE = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
    },
});

// Request interceptor - add auth token and cache busting
api.interceptors.request.use((config) => {
    if (config.url && config.url.endsWith('/') && config.url.length > 1) {
        config.url = config.url.slice(0, -1);
    }
    if (config.method === 'get') {
        config.params = { ...config.params, _t: Date.now() };
    }
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Response interceptor - handle auth errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
                originalRequest._retry = true;
                try {
                    const response = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refreshToken });
                    const { access_token } = response.data?.data?.access_token || response.data?.access_token;
                    localStorage.setItem('access_token', access_token);
                    originalRequest.headers.Authorization = `Bearer ${access_token}`;
                    return api(originalRequest);
                } catch (refreshError) {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('refresh_token');
                    localStorage.removeItem('user');
                    window.location.href = '/login';
                    return Promise.reject(refreshError);
                }
            } else {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export const extractData = (response, key = null) => {
    const data = response?.data?.data || response?.data || response;
    if (key) return data?.[key] || data;
    return data;
};

export const forceRefresh = () => {
    if ('caches' in window) {
        caches.keys().then(names => { names.forEach(name => { caches.delete(name); }); });
    }
};

export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    logout: () => api.post('/auth/logout'),
    refreshToken: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
    getMe: () => api.get('/auth/me'),
    forgotPassword: (data) => api.post('/auth/forgot-password', data),
    resetPassword: (data) => api.post('/auth/reset-password', data),
    verifyResetToken: (token) => api.get(`/auth/verify-reset-token?token=${token}`),
    changePassword: (data) => api.post('/auth/change-password', data),
};

export const salonAPI = {
    getSettings: () => api.get('/salon/settings'),
    updateSettings: (data) => api.put('/salon/settings/update', data),
    updateProfile: (data) => api.put('/salon/settings/update', data),
    updatePaystack: (data) => api.post('/salon/paystack', data),
    updatePaystackSettings: (data) => api.post('/salon/paystack', data),
    updateBookingFee: (data) => api.post('/salon/booking-fee', data),
    updateBookingSettings: (data) => api.post('/salon/booking-fee', data),
    getLogs: (params) => api.get(`/salon/logs${params ? '?' + params : ''}`),
    exportLogs: (format) => api.get('/salon/logs/export', { params: { format }, responseType: 'blob' }),
    getDashboard: () => api.get('/dashboard/stats'),
};

export const remindersAPI = {
    getSettings: () => api.get('/reminders/settings'),
    updateSettings: (data) => api.post('/reminders/settings/update', data),
    sendTestNotification: (data) => api.post('/reminders/test', data),
    sendMessage: (data) => api.post('/reminders/send-message', data),
    updateReminders: (data) => api.post('/reminders/settings/update', data),
};

export const staffAPI = {
    getAll: () => api.get('/staff'),
    create: (data) => api.post('/staff/create', data),
    getOne: (id) => api.get(`/staff/${id}`),
    update: (id, data) => api.put(`/staff/${id}/update`, data),
    delete: (id) => api.delete(`/staff/${id}/delete`),
};

// ==================== SERVICE CATEGORIES API ====================
export const categoriesAPI = {
    getAll: () => api.get('/categories'),
    create: (data) => api.post('/categories/create', data),
    update: (id, data) => api.put(`/categories/${id}/update`, data),
    delete: (id) => api.delete(`/categories/${id}/delete`),
};

export const servicesAPI = {
    getAll: () => api.get('/services'),
    create: (data) => api.post('/services/create', data),
    getOne: (id) => api.get(`/services/${id}`),
    update: (id, data) => api.put(`/services/${id}/update`, data),
    delete: (id) => api.delete(`/services/${id}/delete`),
};

export const clientsAPI = {
    getAll: (params) => api.get('/clients', { params }),
    create: (data) => api.post('/clients/create', data),
    getOne: (id) => api.get(`/clients/${id}`),
    update: (id, data) => api.put(`/clients/${id}/update`, data),
    delete: (id) => api.delete(`/clients/${id}/delete`),
};

export const appointmentsAPI = {
    getAll: (params) => api.get('/appointments', { params }),
    getToday: () => api.get('/appointments/today'),
    getUpcoming: () => api.get('/appointments/upcoming'),
    getStats: (params) => api.get('/appointments/stats', { params }),
    getWeek: (params) => api.get('/appointments', { params }),
    create: (data) => api.post('/appointments/create', data),
    getOne: (id) => api.get(`/appointments/${id}`),
    update: (id, data) => api.put(`/appointments/${id}/update`, data),
    cancel: async (id) => { return await api.post(`/appointments/${id}/cancel`); },
    confirm: async (id) => { return await api.post(`/appointments/${id}/confirm`); },
    complete: async (id) => { return await api.post(`/appointments/${id}/complete`); },
    noShow: (id) => api.post(`/appointments/${id}/no-show`),
    sendReminder: (id) => api.post(`/appointments/${id}/remind`),
    delete: (id) => api.delete(`/appointments/${id}/delete`),
    getDashboardStats: () => api.get('/dashboard/stats'),
};

export const accountingAPI = {
    getTransactions: (params = {}) => api.get('/transactions', { params }),
    getSummary: (params = {}) => api.get('/accounting/summary', { params }),
    createIncome: (data) => api.post('/transactions/create', { type: 'income', amount: data.amount, description: data.description, category: data.category || '', date: data.date }),
    createExpense: (data) => api.post('/transactions/create', { type: 'expense', amount: data.amount, description: data.description, category: data.category || '', date: data.date }),
    createTransaction: (data) => api.post('/transactions/create', data),
    getTransaction: (id) => api.get(`/transactions/${id}`),
    updateTransaction: (id, data) => api.put(`/transactions/${id}/update`, data),
    deleteTransaction: (id) => api.delete(`/transactions/${id}/delete`),
};

export const invoicesAPI = {
    getAll: () => api.get('/invoices'),
    generate: (data) => api.post('/invoices/generate', data, { responseType: 'blob' }),
    getDetail: (id) => api.get(`/invoices/${id}`),
    download: (id) => api.get(`/invoices/${id}/download`, { responseType: 'blob' }),
};

export const subscriptionsAPI = {
    getPlans: () => api.get('/subscriptions/plans'),
    getCurrent: () => api.get('/subscriptions/current'),
    initialize: (data) => api.post('/subscriptions/initialize', data),
    verify: (reference) => api.get(`/subscriptions/verify/${reference}`),
    buyCredits: (data) => api.post('/subscriptions/buy-credits', data),
    cancel: () => api.post('/subscriptions/cancel'),
};

export const publicBookingAPI = {
    getSalon: (salonId) => axios.get(`${API_BASE}/public/salon/${salonId}`),
    getServices: (salonId) => axios.get(`${API_BASE}/public/salon/${salonId}/services`),
    getAvailability: (salonId, date) => axios.get(`${API_BASE}/public/salon/${salonId}/availability`, { params: { date, _t: Date.now() } }),
    createBooking: (data) => axios.post(`${API_BASE}/public/booking/create`, data),
    verifyPayment: (data) => axios.post(`${API_BASE}/public/booking/verify`, data),
    getBusinesses: () => axios.get(`${API_BASE}/public/businesses`),
};

export const healthAPI = {
    check: () => axios.get(`${BACKEND_URL}/health/`),
    ready: () => axios.get(`${BACKEND_URL}/health/ready/`),
    live: () => axios.get(`${BACKEND_URL}/health/live/`),
};

export default api;
